// @flow
import type { EditAction, HiveAction } from '~/core/types/beekeeper';
import type {
    ActionFormAttribute,
    ActionsState,
    ChooseActionOrTemplate,
    ReceiveActions,
    ReceiveTemplatesAction,
    RemoveAction,
    SetActionAttribute,
    SetActionForm,
    TriggerAction
} from './types';
import type { Action } from '~/core/types';

import { handleActions } from 'redux-actions';
import {
    ADD_ACTION_ATTRIBUTE,
    ATTEMPT_CREATE_ACTION,
    ATTEMPT_EDIT_ACTION,
    ATTEMPT_REMOVE_ACTION,
    ATTEMPT_TRIGGER_QUICK_ACTION,
    CANCEL_FORM,
    EDIT_ACTION,
    END_ATTEMPT,
    END_ATTEMPT_CREATE_ACTION,
    GET_ACTIONS,
    GET_TEMPLATES,
    LOAD_FORM,
    RECEIVE_ACTIONS,
    RECEIVE_TEMPLATES,
    REMOVE_ACTION_ATTRIBUTE,
    REMOVE_ACTION_FROM_LIST,
    REMOVE_ACTION_FROM_DELETE_LIST,
    RESET_QUICK_ACTION_STATUS,
    SET_ACTION_ATTRIBUTE,
    SET_ACTION_FORM,
    TRIGGER_QUICK_ACTION,
    IDLE,
    TRIGGERED,
    SUCCESS,
    ERROR
} from './constants';
import handleReduce from '~/core/state/handle-reduce';

const initialState: ActionsState = {
    actionForm: 'none',
    actionsBeingDeleted: [],
    actions: [],
    loadingActions: false,
    loadingTemplates: false,
    savingState: 'done',
    templates: [],
    triggerActionsList: []
};

const actionHandlers = {
    [ADD_ACTION_ATTRIBUTE]: handleReduce((state: ActionsState, { payload: event }: Action<ActionFormAttribute>) => ({
        ...state,
        ...(state.actionForm === 'none' || !event
            ? {}
            : {
                  actionForm: {
                      ...state.actionForm,
                      [event.group]: [...state.actionForm[event.group], event]
                  }
              })
    })),
    [ATTEMPT_CREATE_ACTION]: handleReduce((state: ActionsState) => ({ ...state, savingState: 'saving' })),
    [ATTEMPT_EDIT_ACTION]: handleReduce((state: ActionsState) => ({ ...state, savingState: 'saving' })),
    [ATTEMPT_REMOVE_ACTION]: handleReduce((state: ActionsState, { payload: actionId }: RemoveAction) => ({
        ...state,
        actionsBeingDeleted: [...state.actionsBeingDeleted, actionId],
        savingState: 'saving'
    })),
    [ATTEMPT_TRIGGER_QUICK_ACTION]: handleReduce((state: ActionsState, { payload: actionId }: TriggerAction) => ({
        ...state,
        triggerActionsList: [
            ...state.triggerActionsList.filter(a => a.id !== actionId),
            { id: actionId, displayState: TRIGGERED }
        ]
    })),
    [CANCEL_FORM]: handleReduce((state: ActionsState) => ({
        ...state,
        actionForm: 'none'
    })),
    [EDIT_ACTION]: handleReduce((state: ActionsState, { payload: action }: Action<EditAction>) => {
        const actionsWithEdit = state.actions.map(
            a =>
                a.id !== action.id
                    ? a
                    : {
                          ...a,
                          ...action
                      }
        );
        return { ...state, actions: actionsWithEdit };
    }),
    [END_ATTEMPT]: handleReduce(
        (state: ActionsState) => ({ ...state, actionForm: 'none', savingState: 'done' }),
        (state: ActionsState) => ({ ...state, savingState: 'failed' })
    ),
    [END_ATTEMPT_CREATE_ACTION]: handleReduce(
        (state: ActionsState, { payload: newAction }: { payload: HiveAction }) => ({
            ...state,
            actions: [...state.actions, newAction],
            actionForm: 'none',
            savingState: 'done'
        }),
        (state: ActionsState) => ({ ...state, savingState: 'failed' })
    ),
    [GET_ACTIONS]: handleReduce((state: ActionsState) => ({ ...state, loadingActions: true })),
    [GET_TEMPLATES]: handleReduce((state: ActionsState) => ({ ...state, loadingTemplates: true })),
    [LOAD_FORM]: handleReduce((state: ActionsState, { payload: form }: ChooseActionOrTemplate) => ({
        ...state,
        actionForm: form
    })),
    [RECEIVE_ACTIONS]: handleReduce(
        (state: ActionsState, { payload: actions }: ReceiveActions) => ({
            ...state,
            actions,
            loadingActions: false
        }),
        (state: ActionsState) => ({ ...state, loadingActions: false })
    ),

    [RECEIVE_TEMPLATES]: handleReduce(
        (state: ActionsState, { payload: { templates } }: ReceiveTemplatesAction) => ({
            ...state,
            templates,
            loadingTemplates: false
        }),
        (state: ActionsState) => ({ ...state, loadingTemplates: false })
    ),
    [REMOVE_ACTION_ATTRIBUTE]: handleReduce((state: ActionsState, { payload: event }: Action<ActionFormAttribute>) => ({
        ...state,
        ...(state.actionForm === 'none' || !event
            ? {}
            : {
                  actionForm: {
                      ...state.actionForm,
                      [event.group]: state.actionForm[event.group].filter(
                          e => !(event && e.type === event.type && (!e.id || e.id === event.id))
                      )
                  }
              })
    })),
    [REMOVE_ACTION_FROM_LIST]: handleReduce((state: ActionsState, { payload: actionId }: RemoveAction) => ({
        ...state,
        actions: state.actions.filter(a => a.id !== actionId)
    })),
    [REMOVE_ACTION_FROM_DELETE_LIST]: handleReduce((state: ActionsState, { payload: actionId }: RemoveAction) => ({
        ...state,
        actionsBeingDeleted: state.actionsBeingDeleted.reduce(
            (acc, action) => (action === actionId ? acc : [...acc, action]),
            []
        ),
        actions: state.actions.filter(a => a.id !== actionId)
    })),
    [RESET_QUICK_ACTION_STATUS]: handleReduce((state: ActionsState, { payload: actionId }) => {
        return {
            ...state,
            triggerActionsList: [
                ...state.triggerActionsList.filter(a => a.id !== actionId),
                { id: actionId, displayState: IDLE }
            ]
        };
    }),
    [SET_ACTION_FORM]: handleReduce((state: ActionsState, { payload }: Action<SetActionForm>) => ({
        ...state,
        ...(state.actionForm === 'none' || !payload
            ? {}
            : {
                  actionForm: {
                      ...state.actionForm,
                      [payload.propertyName]: payload.newValue
                  }
              })
    })),
    [SET_ACTION_ATTRIBUTE]: handleReduce((state: ActionsState, { payload }: Action<SetActionAttribute>) => {
        const setAttributeFromGroup = (all, item, index) =>
            payload && payload.attributeIndex === index
                ? [...all, { ...item, [payload.attributeProperty]: payload.newValue }]
                : [...all, item];
        return {
            ...state,
            ...(state.actionForm === 'none' || !payload
                ? {}
                : {
                      actionForm: {
                          ...state.actionForm,
                          [payload.group]: state.actionForm[payload.group].reduce(setAttributeFromGroup, [])
                      }
                  })
        };
    }),
    [TRIGGER_QUICK_ACTION]: handleReduce(
        (state: ActionsState, { payload: actionId }: TriggerAction) => ({
            ...state,
            triggerActionsList: [
                ...state.triggerActionsList.filter(a => a.id !== actionId),
                { id: actionId, displayState: SUCCESS }
            ]
        }),
        (state: ActionsState, { payload: error }: *) => ({
            ...state,
            triggerActionsList: [
                ...state.triggerActionsList.filter(a => a.id !== error.message),
                { id: error.message, displayState: ERROR }
            ]
        })
    )
};

export default handleActions(actionHandlers, initialState);



// WEBPACK FOOTER //
// ./src/core/state/actions/index.js